/**
 * Versión visible del panel.
 *
 * 🔴 La versión que se muestra **la responde la API** (`GET /health`, declarada en
 * `backend/version.py`). Aquí no queda ninguna copia que mantener a mano: una
 * constante escrita en el frontend es una constante que se queda atrás, y el
 * usuario ve un número que no es el que corre. Es la lección de Mi Mariscal, donde
 * el panel decía 1.37.0 con el backend en 1.41.0 y nada avisaba.
 *
 * `VERSION_BUNDLE` es la de `frontend/package.json`, que Vite inyecta al construir.
 * No se muestra como la versión del sistema: solo sirve para **delatar un
 * despliegue a medias** o un navegador sirviendo el bundle viejo desde su caché.
 */
import { useEffect, useState } from "react";

export const VERSION_BUNDLE: string = __APP_VERSION__;

// Se pide una sola vez por carga de página: el número no cambia bajo los pies del
// usuario, y la pintan varias pantallas.
let cache: string | null = null;
let enVuelo: Promise<string> | null = null;

function pedir(): Promise<string> {
  if (cache) return Promise.resolve(cache);
  if (!enVuelo) {
    enVuelo = fetch("/health")
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(String(r.status)))))
      .then((d) => {
        cache = String(d.version || "");
        return cache;
      })
      // Sin API no hay versión que mostrar: se cae a la del bundle, que es lo único
      // que se sabe con certeza. Peor sería no mostrar nada.
      .catch(() => {
        cache = VERSION_BUNDLE;
        return cache;
      });
  }
  return enVuelo;
}

/**
 * `{ version, desfasado }`. `desfasado` = el bundle cargado no es el de la versión
 * que corre en el servidor: hay que recargar sin caché.
 */
export function useVersion(): { version: string; desfasado: boolean } {
  const [version, setVersion] = useState(cache || VERSION_BUNDLE);
  useEffect(() => {
    pedir().then(setVersion);
  }, []);
  return { version, desfasado: !!version && version !== VERSION_BUNDLE };
}
